import { useCallback, useEffect, useMemo, useState } from 'react';

import type { SharePointSearchHit } from '@/lib/api/sharepoint.js';
import type { WorkspaceArtifact } from '@/lib/api/workspace-artifacts.js';

import { type MentionEntry, type MentionSection } from './mention-entry.js';
import { useMentionEntries } from './useMentionEntries.js';

/** The trailing "@token" being typed at the caret, or null. */
export interface MentionState {
  open: boolean;
  query: string;
}

/**
 * The `@name` being typed immediately before the caret — an `@` at the start of a line or right after
 * whitespace, then the part of the file name typed so far.
 *
 * The token takes what file names are made of: letters in any script, digits, dots, dashes and
 * underscores. It cannot take a SPACE, so a name with one in it can only be typed up to the first space —
 * matching is by prefix, exactly as it is in Slack and Notion, and the rest of the name is found by
 * picking the row rather than by typing it out. That is a property of the trigger, not a bug in it.
 *
 * Requiring a line start or whitespace in front of the `@` is what keeps an email address out of it:
 * in `john@example.com` the character before the `@` is a letter, so nothing opens and the sentence is
 * typed the way it was meant.
 */
const MENTION_TOKEN = /(?:^|\s)@([\p{L}\p{N}._-]*)$/u;

export interface MentionPopup {
  /** Non-null while the trailing text matches the trigger — drives `MentionMenu`. */
  state: MentionState | null;
  /** The rows to list, grouped by the source they came from. */
  sections: MentionSection[];
  /** The same rows in one list, in section order — what the arrows walk and Enter picks from. */
  entries: MentionEntry[];
  /** True while a source may still have rows on the way — chooses what an empty list says. */
  searching: boolean;
  /** The highlighted row, held by KEY rather than by position. Null means "the first row". */
  highlightKey: string | null;
  /** Sets the highlighted row directly — wired to the popup's mouse-hover-updates-highlight behavior. */
  setHighlightKey: (key: string) => void;
  /** Cuts the typed `@token` out of the text and runs whatever the row's own action is. */
  selectEntry: (entry: MentionEntry) => void;
  /** Dismisses the popup for the current query — the same latch Escape uses. */
  dismiss: () => void;
  /**
   * The arrows, Enter and Escape, while the popup is open. `true` means the popup took the key and the
   * host must not act on it; `false` means the key was never the popup's and belongs to the host —
   * which is what every key is whenever the popup is shut.
   */
  onKeyDown: (key: string, shift?: boolean) => boolean;
}

/**
 * The `@` popup over a plain text field: what it lists, which row is highlighted, and the keys it owns
 * while it is open.
 *
 * Lifted out of the composer so the automation window can attach a file by typing its name the same way
 * a message can, rather than by growing a second implementation of the same handful of rules. The host
 * keeps the text and the caret — the two differ per field and one of them lives in a Radix dialog — and
 * hands them in; the hook answers with state, rows and one key handler.
 *
 * Three details here are not incidental, and each one was written in answer to a real bug:
 *
 *  - **Escape is a latch on the query, not a close button.** Closing outright would mean the popup
 *    re-opens on the very next render, since the trigger still matches; latching on the query keeps it
 *    shut until the person types something else.
 *  - **The highlight is a row KEY.** The rows arrive from sources on very different clocks, and an index
 *    would quietly slide under a finger already on Enter.
 *  - **The token is cut with the text after the caret preserved**, because the `@` is often typed into
 *    the middle of a sentence rather than at its end.
 */
export function useMentionPopup({
  text,
  caretPos,
  workspaceId,
  suppressed = false,
  onSelectArtifact,
  onSelectSharePointFile,
  onReplaceText,
}: {
  text: string;
  caretPos: number;
  /**
   * Where the files are looked for. Without one there is nothing to search — no artifact registry and no
   * SharePoint connection — so the trigger is dead, exactly as the picker button is simply not there.
   */
  workspaceId: string | null;
  /** Another popup owns the caret (the composer's slash token), so this one must stay out of the way. */
  suppressed?: boolean;
  onSelectArtifact: (artifact: WorkspaceArtifact) => void;
  onSelectSharePointFile: (hit: SharePointSearchHit) => void;
  /** The text with the token cut out, and where the caret goes. The host owns focus. */
  onReplaceText: (text: string, caret: number) => void;
}): MentionPopup {
  /**
   * The `@name` immediately before the caret, or null. Anchored on the caret, so a token the user has
   * typed past no longer matches.
   */
  const query = useMemo<string | null>(() => {
    if (workspaceId === null || suppressed) return null;
    const match = MENTION_TOKEN.exec(text.slice(0, caretPos));
    return match ? (match[1] ?? '') : null;
  }, [text, caretPos, workspaceId, suppressed]);

  /** The query Escape last dismissed the popup for. Cleared as soon as the query changes. */
  const [dismissedQuery, setDismissedQuery] = useState<string | null>(null);
  useEffect(() => {
    setDismissedQuery(null);
  }, [query]);

  const state = useMemo<MentionState | null>(() => {
    if (query === null) return null;
    return { open: query !== dismissedQuery, query };
  }, [query, dismissedQuery]);

  const open = state !== null && state.open;

  const mention = useMentionEntries({
    workspaceId,
    query: open ? state.query : null,
    onSelectArtifact,
    onSelectSharePointFile,
  });

  /** Null means "the first row". Reset per query, and NOT when the rows change, so a source answering
   *  late leaves the highlight where the user put it. */
  const [highlightKey, setHighlightKey] = useState<string | null>(null);
  useEffect(() => {
    setHighlightKey(null);
  }, [query]);

  /** Where the highlight sits today. A key that has left the list falls back to the first row. */
  const highlightIndex = useMemo<number>(() => {
    const found = mention.entries.findIndex(
      (entry) => entry.key === highlightKey,
    );
    return found >= 0 ? found : 0;
  }, [mention.entries, highlightKey]);

  /**
   * Cuts the typed `@token` out of the text — preserving whatever follows the caret — and runs the row's
   * own action. What that action IS belongs to the row: today's two sources attach a file, and a source
   * whose rows mean something else needs nothing here.
   */
  const selectEntry = useCallback(
    (entry: MentionEntry): void => {
      const before = text.slice(0, caretPos);
      const after = text.slice(caretPos);
      const match = MENTION_TOKEN.exec(before);
      /**
       * The match takes in the whitespace character in front of the `@`, and that character is put
       * back exactly as it was found rather than as a space: `MENTION_TOKEN` accepts any `\s`, so
       * flattening it would turn a newline into a space and silently join two lines of a draft written
       * over several — an automation's Prompt field, which uses this same popup, is exactly where an
       * `@` gets typed at the start of a line.
       */
      const whitespaceBeforeToken =
        match === null || match[0].startsWith('@') ? '' : match[0].slice(0, 1);
      const prefix = match
        ? before.slice(0, match.index) + whitespaceBeforeToken
        : before;
      entry.select();
      onReplaceText(prefix + after, prefix.length);
    },
    [text, caretPos, onReplaceText],
  );

  const dismiss = useCallback((): void => {
    if (query !== null) setDismissedQuery(query);
  }, [query]);

  const onKeyDown = useCallback(
    (key: string, shift = false): boolean => {
      if (!open) return false;
      /**
       * ArrowUp/ArrowDown move the highlighted row with wrap-around instead of moving the caret — the
       * popup has no focus of its own, so without this the keys would silently do nothing.
       */
      if (
        (key === 'ArrowDown' || key === 'ArrowUp') &&
        mention.entries.length > 0
      ) {
        const delta = key === 'ArrowDown' ? 1 : -1;
        const next =
          (highlightIndex + delta + mention.entries.length) %
          mention.entries.length;
        setHighlightKey(mention.entries[next]!.key);
        return true;
      }
      if (key === 'Escape') {
        dismiss();
        return true;
      }
      /**
       * Enter picks the highlighted row instead of whatever the host would do with it. Shift+Enter is
       * never the popup's: it is the newline key, and a person holding Shift is writing, not choosing.
       * An open popup with no rows leaves Enter to the host too — there is nothing to pick. Tab picks
       * the same way, as an IDE accepts a completion; Shift+Tab stays the key that moves focus back.
       */
      if (
        (key === 'Enter' || key === 'Tab') &&
        !shift &&
        mention.entries.length > 0
      ) {
        selectEntry(mention.entries[highlightIndex]!);
        return true;
      }
      return false;
    },
    [open, dismiss, mention.entries, highlightIndex, selectEntry],
  );

  return {
    state,
    sections: mention.sections,
    entries: mention.entries,
    searching: mention.searching,
    highlightKey,
    setHighlightKey,
    selectEntry,
    dismiss,
    onKeyDown,
  };
}
